import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import {
  TabContent,
  TabPane,
  Form,
  Row,
  Col,
  FormGroup,
  Label,
  CustomInput,
} from 'reactstrap';
import {
  ContAffiliation,
  H5Styled,
  ContFilterYear,
  BtnOutlineOrange,
  BtnOutlineGreen,
  ContPaginationPage,
} from '../../../../styled-components';
import SearchInput from '../../../../commons/SearchInput';
import generateMonthOptions from '../../../../lib/helpers/generateMonthOptions';
import InsurancesRoyaltyTable from '../InsurancesRoyaltyTable';
import TotalInsuranceRevenuesRow from '../TotalTrInsuranceRevenue';
import ReportingModal from '../../../../commons/ReportingModal';
import generateYearOptions from '../../../../lib/helpers/generateYearOptions';
import { useAuth } from '../../../../commons/Auth';
import variables from '../../../../variables';
import generatePaginationOptions from '../../../../lib/helpers/generatePaginationOptions';
import InsurancesRoyaltyTableBackoffice from '../InsurancesRoyaltyTableBackoffice';
import { USER_ADMIN_TYPES } from '../../../../App';
import moment from 'moment';

const VarRevInsDetailPresentational = ({
  data = {},
  total,
  profile,
  query,
  onFilter,
  onAddCommercialBonus,
}) => {
  const [t] = useTranslation('CONTACTS');
  const [{ newIsAdmin, newUserType = '' }] = useAuth();
  const { categories = {} } = data;
  const { variableRevenuesInsurance = {} } = categories;
  const [amount, setAmount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const alert = useAlert();
  const { data: dataRevInsurances = [] } = variableRevenuesInsurance;

  const origin = window.location.origin;
  let hash = window.location.hash.split('/');
  hash.pop();
  const prevPath =
    origin +
    '/' +
    hash.join('/') +
    `${query.year ? '?year=' + query.year : ''}`;

  const getVariableRevenuesInsuranceTotals = (dataRev) => {
    const netTotals = dataRev.reduce((prev, cur) => prev + cur?.net, 0);
    const grossTotals = dataRev.reduce((prev, cur) => prev + cur?.gross, 0);
    const commercialShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur?.commercialShare,
      0
    );
    const earnedCommercialShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur?.earnedCommercialShare,
      0
    );
    const billableCommercialShareTotals = dataRev.reduce((prev, cur) => {
      return prev + cur?.billableCommercialShare;
    }, 0);
    return {
      netTotals,
      grossTotals,
      commercialShareTotals,
      earnedCommercialShareTotals,
      billableCommercialShareTotals,
    };
  };

  const toggle = ({ selectAll: toggleSelectAll = false } = {}) => {
    if (isOpen) {
      setSelectAll(false);
      return setIsOpen(false);
    }

    setSelectAll(toggleSelectAll);

    const validRows = dataRevInsurances.map((row, index) => {
      if (row.checked || toggleSelectAll) {
        return {
          ...row,
          checked:
            row.billableCommercialShare -
              row?.iafs?.reduce((acc, iaf) => acc + iaf.amount, 0) >
            0,
        };
      }
      return row;
    });

    const checkedRows = validRows.filter(({ checked }) => checked);

    if (profile.isSubAgency !== true) {
      alert.error(t('ROYALTY:NO_SUB_AGENCY'));
      return;
    }

    if (checkedRows.length) {
      const billableTotal = checkedRows.reduce(
        (acc, { billableCommercialShare = 0 }) =>
          acc + billableCommercialShare || 0,
        0
      );
      const alreadyBilled = checkedRows.reduce((acc, { iafs = [] }) => {
        return (
          acc +
            iafs.reduce((acc, { amount = 0 }) => {
              return acc + amount || 0;
            }, 0) || 0
        );
      }, 0);
      setAmount(+(billableTotal - alreadyBilled).toFixed(2));
      setIsOpen(!isOpen);
    } else {
      const message = toggleSelectAll
        ? t('ROYALTY:NO_ROW_IAF_SELECTABLE')
        : t('ROYALTY:SELECT_AT_LEAST_ONE');
      alert.error(message);
    }
  };

  const onSubmit = (formData) => {
    const validRows = dataRevInsurances.map((row, index) => {
      if (row.checked || selectAll) {
        return {
          ...row,
          checked:
            row.billableCommercialShare -
              row?.iafs?.reduce((acc, iaf) => acc + iaf.amount, 0) >
            0,
        };
      }
      return row;
    });

    const checkedRows = validRows.filter(({ checked }) => checked);

    const accountabilityYear = moment(checkedRows[0]?.start_date).year();
        for (const row of checkedRows) {
          const rowYear = moment(row.start_date).year();
          if (rowYear !== accountabilityYear) {
            alert.error(
              'Le assicurazioni selezionate presentano anni di inizio differenti.'
            );
            toggle();
            return;
          }
        }

    const data = {
      ...formData,
      accountability: accountabilityYear,
      iafType: 'INSURANCES',
      insurances: checkedRows.map(
        ({
          _id,
          branch,
          policy_code,
          insurance_code,
          start_date,
          end_date,
          gross,
          net,
          shareToInvoice,
          shareToInvoicePerc,
          subAgencyPerc,
          panoramicaShare,
          customerId,
          admin,
          earnedCommercialShare,
          commercialShare,
          agencyType,
        }) => ({
          _id,
          branch,
          policy_code,
          insurance_code,
          start_date,
          end_date,
          gross,
          net,
          shareToInvoice,
          shareToInvoicePerc,
          subAgencyPerc,
          panoramicaShare,
          customerId,
          admin,
          earnedCommercialShare,
          commercialShare,
          agencyType,
        })
      ),
      amount,
    };
    onAddCommercialBonus(data);
    toggle();
  };

  return (
    <Form onSubmit={(e) => e.preventDefault()}>
      <TabContent activeTab={'1'}>
        <TabPane tabId="1">
          <ContFilterYear>
            <Row>
              <Col xs="6">
                <Label>{t('TITLE_FILTER_YEAR')}</Label>
              </Col>
              <Col xs="6">
                <FormGroup>
                  <CustomInput
                    type="select"
                    id="selectYear"
                    name="year"
                    value={query && query.year}
                    onChange={({ target: { value: year } }) =>
                      onFilter({ year })
                    }
                  >
                    {generateYearOptions()}
                  </CustomInput>
                </FormGroup>
              </Col>
            </Row>
          </ContFilterYear>
          <div
            style={{
              fontWeight: 700,
              color: '#333333',
              marginBottom: '20px',
              fontSize: '20px',
            }}
          >
            <a
              style={{
                color: variables.primary,
              }}
              href={prevPath}
            >
              Rendicontazioni network
            </a>{' '}
            / <a>{t('INSURANCES:ROYALTIES_TITLE')}</a>
          </div>
          <ContAffiliation key={2}>
            <Row>
              <Col xs="6">
                <H5Styled>{t('INSURANCES:ROYALTIES_TITLE')}</H5Styled>
              </Col>
              <Col xs="6" className="text-right">
                {newIsAdmin && (
                  <BtnOutlineGreen
                    type="button"
                    onClick={toggle}
                    style={{ marginRight: '16px' }}
                  >
                    {t('ROYALTY:INVITE_TO_INVOICE')}
                  </BtnOutlineGreen>
                )}
                {newIsAdmin && (
                  <BtnOutlineGreen
                    type="button"
                    onClick={() => toggle({ selectAll: true })}
                    style={{ marginRight: '16px' }}
                  >
                    {t('ROYALTY:INVITE_TO_INVOICE_ALL')}
                  </BtnOutlineGreen>
                )}
              </Col>
            </Row>
            <br />
            <Row className="search-add-cont m-t-20" style={{ rowGap: '1rem' }}>
              <Col sm="12" md="6" lg="3" className="select-wrapper">
                <ContFilterYear
                  className="no-width"
                  style={{ marginBottom: '0' }}
                >
                  <Row>
                    <Col xs="6">
                      <Label>{t('COMMON:TITLE_FILTER_SIGNIN_MONTH')}</Label>
                    </Col>
                    <Col xs="6">
                      <FormGroup>
                        <CustomInput
                          type="select"
                          id="insuranceStartMonth"
                          name="insuranceStartMonth"
                          value={query && query.insuranceStartMonth}
                          onChange={({
                            target: { value: insuranceStartMonth },
                          }) => onFilter({ insuranceStartMonth })}
                        >
                          {generateMonthOptions()}
                        </CustomInput>
                      </FormGroup>
                    </Col>
                  </Row>
                </ContFilterYear>
              </Col>

              <Col sm="6" md="6" lg="3" xl="3">
                <SearchInput
                  query={{ search: query && query.policy_code }}
                  onFilter={({ search }) => onFilter({ policy_code: search })}
                  label={t('SEARCH:POLICY_CODE')}
                />
              </Col>

              <Col sm="6" md="6" lg="3" xl="3">
                <SearchInput
                  query={{ search: query && query.admin_cf }}
                  onFilter={({ search }) => onFilter({ admin_cf: search })}
                  label={t('SEARCH:REFERENCE_ID')}
                />
              </Col>

              <Col sm="6" md="6" lg="3" xl="3">
                <SearchInput
                  query={{ search: query && query.supplier }}
                  onFilter={({ search }) => onFilter({ supplier: search })}
                  label={t('SEARCH:BY_SUPPLIER')}
                />
              </Col>

              <Col sm="6" md="6" lg="3" xl="3">
                <SearchInput
                  query={{ search: query && query.customer }}
                  onFilter={({ search }) => onFilter({ customer: search })}
                  label={t('SEARCH:CUSTOMER')}
                />
              </Col>

              <Col sm="12" md="6" lg="3" xl="3" className="select-wrapper">
                <ContFilterYear
                  className="no-width"
                  style={{ marginBottom: '0' }}
                >
                  <Row>
                    <Col xs="6">
                      <Label>Tipologia cliente</Label>
                    </Col>
                    <Col xs="6">
                      <FormGroup>
                        <CustomInput
                          type="select"
                          id="customerType"
                          name="customerType"
                          value={query.customerType}
                          onChange={({ target: { value: customerType } }) =>
                            onFilter({ customerType })
                          }
                        >
                          <option value="" defaultChecked>
                            Tutti
                          </option>
                          <option value="condos">Condomini</option>
                          <option value="customerExtra">Clienti privati</option>
                        </CustomInput>
                      </FormGroup>
                    </Col>
                  </Row>
                </ContFilterYear>
              </Col>

              <Col sm="12" md="6" lg="3" xl="3" className="select-wrapper">
                <ContFilterYear
                  className="no-width"
                  style={{ marginBottom: '0' }}
                >
                  <Row>
                    <Col xs="6">
                      <Label>{t('SEARCH:AGENCY_TYPE')}</Label>
                    </Col>
                    <Col xs="6">
                      <FormGroup>
                        <CustomInput
                          type="select"
                          id="agencyType"
                          name="agencyType"
                          value={query.agencyType}
                          onChange={({ target: { value: agencyType } }) =>
                            onFilter({ agencyType })
                          }
                        >
                          <option value="" defaultChecked>
                            Tutti
                          </option>
                          <option value="VITTORIA">VITTORIA</option>
                          <option value="ITAS">ITAS</option>
                          <option value="ITALIANA_ASSICURAZIONI">
                            ITALIANA ASSICURAZIONI
                          </option>
                        </CustomInput>
                      </FormGroup>
                    </Col>
                  </Row>
                </ContFilterYear>
              </Col>

              <Col sm="12" md="6" lg="3" xl="3" className="select-wrapper">
                <ContFilterYear
                  className="no-width"
                  style={{ marginBottom: '0' }}
                >
                  <Row>
                    <Col xs="6">
                      <Label>Stato assicurazione</Label>
                    </Col>
                    <Col xs="6">
                      <FormGroup>
                        <CustomInput
                          type="select"
                          id="active"
                          name="active"
                          value={query.active}
                          onChange={({ target: { value: active } }) =>
                            onFilter({ active })
                          }
                        >
                          <option value="" defaultChecked>
                            Tutti
                          </option>
                          <option value="active">Attive</option>
                          <option value="terminated">Scadute</option>
                        </CustomInput>
                      </FormGroup>
                    </Col>
                  </Row>
                </ContFilterYear>
              </Col>
            </Row>

            <br />
            {(newUserType === USER_ADMIN_TYPES.SUPER_ADMIN && (
              <InsurancesRoyaltyTable
                data={dataRevInsurances || []}
                total={total}
                query={query}
                onFilter={onFilter}
                profile={profile}
                showEmptyTableMessage={true}
                bottomRow={
                  <TotalInsuranceRevenuesRow
                    totals={getVariableRevenuesInsuranceTotals(
                      dataRevInsurances
                    )}
                    route="variable-revenues-insurances"
                    activeRoute={false}
                  />
                }
              />
            )) || (
              <InsurancesRoyaltyTableBackoffice
                data={dataRevInsurances || []}
                total={total}
                query={query}
                onFilter={onFilter}
                showEmptyTableMessage={true}
                bottomRow={
                  <TotalInsuranceRevenuesRow
                    totals={getVariableRevenuesInsuranceTotals(
                      dataRevInsurances
                    )}
                    route="variable-revenues-insurances"
                    activeRoute={false}
                  />
                }
              />
            )}

            <ContPaginationPage>
              <p>{t('COMMON:PAGINATION_PER_PAGE')}</p>

              <FormGroup>
                <CustomInput
                  type="select"
                  className="pagination-select"
                  id="perPage"
                  name="perPage"
                  value={query.limit}
                  onChange={({ target: { value: limit } }) => {
                    onFilter({ limit });
                  }}
                >
                  {generatePaginationOptions()}
                </CustomInput>
              </FormGroup>
            </ContPaginationPage>
          </ContAffiliation>
        </TabPane>
      </TabContent>
      <ReportingModal
        toggle={toggle}
        isOpen={isOpen}
        onSubmit={onSubmit}
        amount={amount}
        query={query}
      />
    </Form>
  );
};

export default VarRevInsDetailPresentational;
